import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Container,
  Row,
  Col,
  Form,
  Card,
  Alert,
  Spinner,
  InputGroup,
  Accordion,
  Modal,
} from "react-bootstrap";
import {
  FaSearch,
  FaTrashAlt,
  FaCar,
  FaUser,
  FaPhone,
  FaWeightHanging,
  FaRuler,
  FaChevronDown,
  FaChevronUp,
  FaIdCard,
  FaTruck,
  FaEdit,
  FaCalendarAlt,
  FaClock,
  FaEye,
  FaTimes,
  FaBox,
  FaEyeSlash,
  FaEnvelope,
  FaMapMarkerAlt,
  FaBuilding,
  FaCheckCircle,
  FaStar,
  FaBoxes,
} from "react-icons/fa";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import "../clientTable/ClientTable.css";
import "../driverTable/PageOne/DarkTheme.css";
import "../../assets/css/Auftraege.css";
import NavbarMenu from "../heroSection/NavbarMenu";
import { userAPI } from "../../api/apiSystem";
import PaginationComponent from "../PaginationComponent";
import { useSearchFilter } from "../useSearchFilter";
import ClientChatBox from "../chat/ClientChatBox";

// Vehicle images
import caddyImg from "../../assets/images/driver_transport_icon/pakete.png";
import busImg from "../../assets/images/driver_transport_icon/palette.png";
import lkwImg from "../../assets/images/driver_transport_icon/moving.png";
import truckImg from "../../assets/images/driver_transport_icon/furniture.png";
import { useNavigate } from "react-router-dom";

const PER_PAGE_OPTIONS = [10, 25, 50];

const INITIAL_FORM_DATA = {
  company_name: "",
  firstname: "",
  lastname: "",
  nicname: "",
  email: "",
  password: "",
  password_confirmation: "",
  street_address: "",
  addressone: "",
  postal_code: "",
};

export default function EmployeeTable() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // ── Data states ─────────────────────────────────────────────
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [userType] = useState(
    typeof window !== "undefined" ? localStorage.getItem("userType") : null,
  );

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  // ── Form states ─────────────────────────────────────────────
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [validationErrors, setValidationErrors] = useState({});

  // ── Edit states ─────────────────────────────────────────────
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editFormData, setEditFormData] = useState(INITIAL_FORM_DATA);
  const [editLoading, setEditLoading] = useState(false);
  const [editErrors, setEditErrors] = useState({});

  // ── Toggle / Delete states ──────────────────────────────────
  const [togglingId, setTogglingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  // ── Password visibility ─────────────────────────────────────
  const [showPasswords, setShowPasswords] = useState({
    password: false,
    password_confirmation: false,
  });

  // ── UI states ───────────────────────────────────────────────
  const [activeKey, setActiveKey] = useState(null);
  const [offCanvasOpen, setOffCanvasOpen] = useState(null);

  // ── Filtered data ───────────────────────────────────────────
  const filteredData = useSearchFilter(employees, searchTerm, [
    "firstname",
    "lastname",
    "nicname",
    "email",
    "company_name",
  ]);

  const displayData = searchTerm ? filteredData : employees;

  // ═══════════════════════════════════════════════════════════════
  // Helpers
  // ═══════════════════════════════════════════════════════════════
  const togglePassword = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const getPasswordStrength = (password) => {
    if (!password) return { level: 0, label: "", color: "#e5e7eb" };
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 2)
      return { level: 1, label: t("common.weak", "Weak"), color: "#dc2626" };
    if (score === 3)
      return { level: 2, label: t("common.medium", "Medium"), color: "#d97706" };
    if (score === 4)
      return { level: 3, label: t("common.strong", "Strong"), color: "#16a34a" };
    return { level: 4, label: t("common.veryStrong", "Very strong"), color: "#059669" };
  };

  const getFullName = (employee) => {
    const parts = [employee?.firstname, employee?.lastname].filter(Boolean);
    return parts.length ? parts.join(" ") : "N/A";
  };

  const renderHighlightedText = (text, term) => {
    if (!term || !text) return text || "N/A";
    const regex = new RegExp(`(${term})`, "gi");
    const parts = text.toString().split(regex);
    return (
      <span>
        {parts.map((part, i) =>
          regex.test(part) ? (
            <mark key={i} className="bg-warning">
              {part}
            </mark>
          ) : (
            part
          ),
        )}
      </span>
    );
  };

  const getVehicleImage = (type) => {
    const typeLower = (type || "").toLowerCase();
    if (typeLower.includes("caddy")) return caddyImg;
    if (typeLower.includes("bus")) return busImg;
    if (typeLower.includes("lkw") || typeLower.includes("truck")) return lkwImg;
    return truckImg;
  };

  const toggleOffCanvas = (index) => {
    setOffCanvasOpen(offCanvasOpen === index ? null : index);
  };

  // ═══════════════════════════════════════════════════════════════
  // Fetch Employees
  // ═══════════════════════════════════════════════════════════════
  const fetchEmployees = useCallback(
    async (page = 1, perPage = pagination.per_page) => {
      setLoading(true);
      setError("");

      try {
        const params = { page, per_page: perPage };
        const response = await userAPI.employees.index(params);

        if (response.success) {
          let employeesData = [];
          let metaData = {};

          if (Array.isArray(response.data)) {
            employeesData = response.data;
            metaData = {
              current_page: page,
              last_page: Math.ceil(response.data.length / perPage),
              per_page: perPage,
              total: response.data.length,
            };
          } else if (response.data?.data && Array.isArray(response.data.data)) {
            employeesData = response.data.data;
            metaData = response.data.meta || {
              current_page: page,
              last_page: 1,
              per_page: perPage,
              total: response.data.data.length,
            };
          } else {
            employeesData = [];
            metaData = {
              current_page: page,
              last_page: 1,
              per_page: perPage,
              total: 0,
            };
          }

          setEmployees(employeesData);
          setPagination(metaData);
        } else {
          throw new Error(response.message || t("errors.fetchEmployees"));
        }
      } catch (err) {
        console.error("❌ Error fetching employees:", err);
        setError(err.message || t("errors.fetchError"));
        setEmployees([]);
      } finally {
        setLoading(false);
      }
    },
    [pagination.per_page, t],
  );

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handlePageChange = (page) => fetchEmployees(page);

  const handlePerPageChange = (e) => {
    const newPerPage = parseInt(e.target.value);
    setPagination((prev) => ({ ...prev, per_page: newPerPage }));
    fetchEmployees(1, newPerPage);
  };

  const clearSearch = () => setSearchTerm("");

  // ═══════════════════════════════════════════════════════════════
  // Create employee
  // ═══════════════════════════════════════════════════════════════
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_DATA);
    setValidationErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.password_confirmation) {
      setError(t("errors.passwordMismatch") || "Passwords do not match!");
      return;
    }

    setFormLoading(true);
    setError("");
    setSuccess("");
    setValidationErrors({});

    try {
      const employeeData = {
        photo: formData.photo,
        company_name: formData.company_name,
        firstname: formData.firstname,
        lastname: formData.lastname,
        nicname: formData.nicname,
        email: formData.email.trim(),
        password: formData.password,
        password_confirmation: formData.password_confirmation,
        street_address: formData.street_address,
        addressone: formData.addressone,
        postal_code: formData.postal_code,
      };

      const response = await userAPI.employees.store(employeeData);

      if (response.success) {
        await Swal.fire({
          icon: "success",
          title: t("alerts.success"),
          text: t("alerts.employeeAdded"),
          confirmButtonColor: "#ff7b00",
          timer: 2000,
        });

        resetForm();
        await fetchEmployees(pagination.current_page);
      } else {
        throw new Error(response.message || t("errors.addEmployee"));
      }
    } catch (err) {
      console.error("❌ Error adding employee:", err);
      await Swal.fire({
        icon: "error",
        title: t("alerts.error"),
        text: err.message,
        confirmButtonColor: "#ff7b00",
      });

      if (err.errors) {
        setValidationErrors(err.errors);
        setError(
          t("errors.validationFailed") ||
            "Please check the form for validation errors.",
        );
      } else {
        setError(err.message || t("errors.addEmployee"));
      }
    } finally {
      setFormLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // Toggle can_create_orders
  // ═══════════════════════════════════════════════════════════════
  const handleToggleCreateOrders = async (employee) => {
    const id = employee?.id;
    if (!id) return;

    setTogglingId(id);

    try {
      const newValue = employee.can_create_orders === 1 ? 0 : 1;

      const response = await userAPI.employees.toggleCreateOrders(id, {
        can_create_orders: newValue,
      });

      if (response.success) {
        setEmployees((prev) =>
          prev.map((emp) =>
            emp.id === id ? { ...emp, can_create_orders: newValue } : emp,
          ),
        );

        Swal.fire({
          icon: "success",
          title: t("alerts.success", "Success"),
          text:
            newValue === 1
              ? t(
                  "employeesTable.canCreateOrdersEnabled",
                  "Employee can now create orders",
                )
              : t(
                  "employeesTable.canCreateOrdersDisabled",
                  "Employee can no longer create orders",
                ),
          confirmButtonColor: "#ff7b00",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        throw new Error(
          response.message || t("errors.toggleFailed", "Toggle failed"),
        );
      }
    } catch (err) {
      console.error("❌ Toggle error:", err);
      Swal.fire({
        icon: "error",
        title: t("alerts.error", "Error"),
        text: err.message || t("errors.generic", "Something went wrong"),
        confirmButtonColor: "#ff7b00",
      });
    } finally {
      setTogglingId(null);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // Edit employee
  // ═══════════════════════════════════════════════════════════════
  const handleOpenEdit = (employee) => {
    setEditingEmployee(employee);
    setEditFormData({
      company_name: employee.company_name || "",
      firstname: employee.firstname || "",
      lastname: employee.lastname || "",
      nicname: employee.nicname || "",
      email: employee.email || "",
      password: "",
      password_confirmation: "",
      street_address: employee.street_address || "",
      addressone: employee.addressone || "",
      postal_code: employee.postal_code || "",
    });
    setEditErrors({});
    setShowEditModal(true);
  };

  const handleOpenBooking = (employeeId) => {
    if (!employeeId) {
      console.warn("⚠️ No employee id provided");
      return;
    }
    navigate(`/user/employees/${employeeId}/booking`);
  };


  const handleCloseEdit = () => {
    setShowEditModal(false);
    setEditingEmployee(null);
    setEditFormData(INITIAL_FORM_DATA);
    setEditErrors({});
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingEmployee?.id) return;

    if (editFormData.password) {
      if (editFormData.password !== editFormData.password_confirmation) {
        setEditErrors({
          password_confirmation: ["Passwords do not match"],
        });
        return;
      }
    }

    setEditLoading(true);
    setEditErrors({});

    try {
      const payload = {
        company_name: editFormData.company_name,
        firstname: editFormData.firstname,
        lastname: editFormData.lastname,
        nicname: editFormData.nicname,
        email: editFormData.email.trim(),
        street_address: editFormData.street_address,
        addressone: editFormData.addressone,
        postal_code: editFormData.postal_code,
      };

      if (editFormData.password) {
        payload.password = editFormData.password;
        payload.password_confirmation = editFormData.password_confirmation;
      }

      const response = await userAPI.employees.update(
        editingEmployee.id,
        payload,
      );

      if (response.success) {
        await Swal.fire({
          icon: "success",
          title: t("alerts.success", "Success"),
          text: t("employeesTable.updatedSuccess", "Employee updated"),
          confirmButtonColor: "#ff7b00",
          timer: 1800,
          showConfirmButton: false,
        });

        handleCloseEdit();
        await fetchEmployees(pagination.current_page);
      } else {
        throw new Error(response.message || t("errors.updateFailed"));
      }
    } catch (err) {
      console.error("❌ Update error:", err);

      if (err.errors) {
        setEditErrors(err.errors);
      } else {
        setEditErrors({ general: [err.message] });
      }

      Swal.fire({
        icon: "error",
        title: t("alerts.error", "Error"),
        text: err.message || t("errors.generic", "Something went wrong"),
        confirmButtonColor: "#ff7b00",
      });
    } finally {
      setEditLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // Delete employee
  // ═══════════════════════════════════════════════════════════════
  const handleDeleteEmployee = async (employee) => {
    const id = employee?.id;
    if (!id) return;

    const result = await Swal.fire({
      title: t("alerts.confirmDelete", "Are you sure?"),
      text: t(
        "employeesTable.deleteWarning",
        "This employee will be permanently deleted.",
      ),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("actions.delete", "Delete"),
      cancelButtonText: t("actions.cancel", "Cancel"),
    });

    if (!result.isConfirmed) return;

    setDeleteId(id);

    try {
      const response = await userAPI.employees.delete(id);

      if (response.success) {
        await Swal.fire({
          icon: "success",
          title: t("alerts.deleted", "Deleted"),
          text: t("employeesTable.deletedSuccess", "Employee deleted"),
          confirmButtonColor: "#ff7b00",
          timer: 1800,
          showConfirmButton: false,
        });

        await fetchEmployees(pagination.current_page);
      } else {
        throw new Error(response.message || t("errors.deleteFailed"));
      }
    } catch (err) {
      console.error("❌ Delete error:", err);
      Swal.fire({
        icon: "error",
        title: t("alerts.error", "Error"),
        text: err.message || t("errors.generic", "Something went wrong"),
        confirmButtonColor: "#ff7b00",
      });
    } finally {
      setDeleteId(null);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // Reusable: Employee Details (Desktop + Mobile)
  // ═══════════════════════════════════════════════════════════════
  const renderEmployeeDetails = (employee) => (
    <>
      {/* Personal Info */}
      <div className="dark-section mb-3">
        <div className="dark-section-title">
          <FaUser />
          <span>{t("employeesTable.personalInfo", "Personal Info")}</span>
        </div>
        <div className="dark-section-body">
          <Row className="g-3">
            <Col md={6}>
              <div className="dark-vehicle-info-block">
                <div className="dark-info-label">
                  {t("employeesTable.fullName", "Full Name")}
                </div>
                <div className="dark-info-value">
                  <FaUser style={{ color: "#ff7b00" }} />
                  <span className="dark-text-white">
                    {getFullName(employee)}
                  </span>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="dark-vehicle-info-block">
                <div className="dark-info-label">
                  {t("employeesTable.nickname", "Nickname")}
                </div>
                <div className="dark-info-value">
                  <FaUser style={{ color: "#ff7b00" }} />
                  <span className="dark-text-white">
                    {employee.nicname || "N/A"}
                  </span>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="dark-vehicle-info-block">
                <div className="dark-info-label">
                  {t("employeesTable.email", "Email")}
                </div>
                <div className="dark-info-value">
                  <FaEnvelope style={{ color: "#ff7b00" }} />
                  <span className="dark-text-white">
                    {employee.email || "N/A"}
                  </span>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="dark-vehicle-info-block">
                <div className="dark-info-label">
                  {t("employeesTable.role", "Role")}
                </div>
                <div className="dark-info-value">
                  <FaUser style={{ color: "#ff7b00" }} />
                  <span className="dark-text-white">
                    {employee.role_name || "N/A"}
                  </span>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Company Info */}
      {(employee.company_name ||
        employee.tax_number ||
        employee.local_court ||
        employee.responsible_person) && (
        <div className="dark-section mb-3">
          <div className="dark-section-title">
            <FaBuilding />
            <span>{t("employeesTable.companyInfo", "Company Info")}</span>
          </div>
          <div className="dark-section-body">
            <Row className="g-3">
              {employee.company_name && (
                <Col md={6}>
                  <div className="dark-vehicle-info-block">
                    <div className="dark-info-label">
                      {t("employeesTable.companyName", "Company")}
                    </div>
                    <div className="dark-info-value">
                      <FaBuilding style={{ color: "#ff7b00" }} />
                      <span className="dark-text-white">
                        {employee.company_name}
                      </span>
                    </div>
                  </div>
                </Col>
              )}
              {employee.tax_number && (
                <Col md={6}>
                  <div className="dark-vehicle-info-block">
                    <div className="dark-info-label">
                      {t("employeesTable.taxNumber", "Tax Number")}
                    </div>
                    <div className="dark-info-value">
                      <span className="dark-text-white">
                        {employee.tax_number}
                      </span>
                    </div>
                  </div>
                </Col>
              )}
              {employee.local_court && (
                <Col md={6}>
                  <div className="dark-vehicle-info-block">
                    <div className="dark-info-label">
                      {t("employeesTable.localCourt", "Local Court")}
                    </div>
                    <div className="dark-info-value">
                      <span className="dark-text-white">
                        {employee.local_court}
                      </span>
                    </div>
                  </div>
                </Col>
              )}
              {employee.responsible_person && (
                <Col md={6}>
                  <div className="dark-vehicle-info-block">
                    <div className="dark-info-label">
                      {t(
                        "employeesTable.responsiblePerson",
                        "Responsible Person",
                      )}
                    </div>
                    <div className="dark-info-value">
                      <span className="dark-text-white">
                        {employee.responsible_person}
                      </span>
                    </div>
                  </div>
                </Col>
              )}
            </Row>
          </div>
        </div>
      )}

      {/* Address Info */}
      <div className="dark-section mb-3">
        <div className="dark-section-title">
          <FaMapMarkerAlt />
          <span>{t("employeesTable.addressInfo", "Address")}</span>
        </div>
        <div className="dark-section-body">
          <Row className="g-3">
            <Col md={6}>
              <div className="dark-vehicle-info-block">
                <div className="dark-info-label">
                  {t("employeesTable.streetAddress", "Street")}
                </div>
                <div className="dark-info-value">
                  <FaMapMarkerAlt style={{ color: "#ff7b00" }} />
                  <span className="dark-text-white">
                    {employee.street_address || "N/A"}
                  </span>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="dark-vehicle-info-block">
                <div className="dark-info-label">
                  {t("employeesTable.addressOne", "Address 2")}
                </div>
                <div className="dark-info-value">
                  <span className="dark-text-white">
                    {employee.addressone || "N/A"}
                  </span>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="dark-vehicle-info-block">
                <div className="dark-info-label">
                  {t("employeesTable.postalCode", "Postal Code")}
                </div>
                <div className="dark-info-value">
                  <span className="dark-text-white">
                    {employee.postal_code || "N/A"}
                  </span>
                </div>
              </div>
            </Col>
            {employee.telphone && (
              <Col md={6}>
                <div className="dark-vehicle-info-block">
                  <div className="dark-info-label">
                    {t("employeesTable.phone", "Phone")}
                  </div>
                  <div className="dark-info-value">
                    <FaPhone style={{ color: "#ff7b00" }} />
                    <span className="dark-text-white">{employee.telphone}</span>
                  </div>
                </div>
              </Col>
            )}
          </Row>
        </div>
      </div>

      {/* Account Status */}
      <div className="dark-section">
        <div className="dark-section-title">
          <FaCheckCircle />
          <span>{t("employeesTable.accountStatus", "Account Status")}</span>
        </div>
        <div className="dark-section-body">
          <Row className="g-3">
            <Col md={6}>
              <div className="dark-vehicle-info-block">
                <div className="dark-info-label">
                  {t("employeesTable.emailVerified", "Email Verified")}
                </div>
                <div className="dark-info-value">
                  <span
                    className={
                      employee.email_checked === 1
                        ? "text-success fw-bold"
                        : "text-danger fw-bold"
                    }
                  >
                    {employee.email_checked === 1
                      ? t("common.yes", "Yes")
                      : t("common.no", "No")}
                  </span>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="dark-vehicle-info-block">
                <div className="dark-info-label">
                  {t("employeesTable.confirmed", "Confirmed")}
                </div>
                <div className="dark-info-value">
                  <span
                    className={
                      employee.is_confirmed === 1
                        ? "text-success fw-bold"
                        : "text-danger fw-bold"
                    }
                  >
                    {employee.is_confirmed === 1
                      ? t("common.yes", "Yes")
                      : t("common.no", "No")}
                  </span>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </>
  );

  // ═══════════════════════════════════════════════════════════════
  // Reusable: Actions Panel (Toggle + Edit + Delete)
  // ═══════════════════════════════════════════════════════════════
  const renderActions = (employee) => (
    <div className="dark-section mt-3">
      <div className="dark-section-title">
        <FaEdit />
        <span>{t("employeesTable.actions", "Actions")}</span>
      </div>
      <div className="dark-section-body">
        <Row className="g-3 align-items-center flex-wrap">
          <Col md={6}>
            <div
              className="d-flex align-items-center justify-content-between p-3 rounded-3"
              style={{
                background: "rgba(255,123,0,0.06)",
                border: "1px solid rgba(255,123,0,0.15)",
              }}
            >
              <div className="d-flex align-items-center gap-2">
                <FaBox style={{ color: "#ff7b00" }} />
                <div>
                  <div
                    className="dark-text-white fw-semibold"
                    style={{ fontSize: "0.9rem" }}
                  >
                    {t("employeesTable.canCreateOrders", "Can create orders")}
                  </div>
                  <small className="dark-text-muted">
                    {employee.can_create_orders === 1
                      ? t("common.enabled", "Enabled")
                      : t("common.disabled", "Disabled")}
                  </small>
                </div>
              </div>

              <Form.Check
                type="switch"
                id={`toggle-create-orders-${employee.id}`}
                checked={employee.can_create_orders === 1}
                onChange={() => handleToggleCreateOrders(employee)}
                disabled={togglingId === employee.id}
                style={{ transform: "scale(1.3)" }}
              />
            </div>
          </Col>

          <Col md={6}>
            <div className="d-flex gap-2 justify-content-end flex-wrap">
              <Button
                variant="outline-success"
                onClick={() => handleOpenBooking(employee.id)}
                style={{
                  borderRadius: "10px",
                  padding: "8px 20px",
                  fontWeight: 600,
                }}
              >
                <FaBoxes className="me-2" />
                {t("ordersOverview", "Bookings")}
              </Button>

              <Button
                variant="outline-warning"
                onClick={() => handleOpenEdit(employee)}
                style={{
                  borderRadius: "10px",
                  padding: "8px 20px",
                  fontWeight: 600,
                }}
              >
                <FaEdit className="me-2" />
                {t("actions.edit", "Edit")}
              </Button>

              <Button
                variant="outline-danger"
                onClick={() => handleDeleteEmployee(employee)}
                disabled={deleteId === employee.id}
                style={{
                  borderRadius: "10px",
                  padding: "8px 20px",
                  fontWeight: 600,
                }}
              >
                {deleteId === employee.id ? (
                  <>
                    <Spinner
                      as="span"
                      size="sm"
                      animation="border"
                      className="me-2"
                    />
                    {t("common.deleting", "Deleting...")}
                  </>
                ) : (
                  <>
                    <FaTrashAlt className="me-2" />
                    {t("actions.delete", "Delete")}
                  </>
                )}
              </Button>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );

  const getScoreColor = (score) => {
    if (score >= 4) return "#0d9f6e";      // أخضر
    if (score >= 3) return "#d97706";      // برتقالي
    return "#dc2626";                       // أحمر
  };

  // ═══════════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════════
  return (
    <>
      <NavbarMenu />
      <Container
        fluid
        className="p-4 vehicle-table-container"
        style={{ minHeight: "100vh" }}
      >
        <div className="p-4">
          {/* Title */}
          <h4 className="page-title mb-4">{t("employeesTable.title")}</h4>

          {/* Search + Show Entries */}
          <Row className="mb-3 align-items-center">
            <Col
              xs={12}
              md={6}
              className="d-flex align-items-center mb-2 mb-md-0"
            >
              <span className="me-2 fw-bold dark-text-white">
                {t("clientTable.show")}
              </span>
              <Form.Select
                size="sm"
                style={{ width: "80px" }}
                value={pagination.per_page}
                onChange={handlePerPageChange}
                disabled={loading}
                className="dark-bg-dark dark-text-white border-secondary"
              >
                {PER_PAGE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Select>
              <span className="ms-2 dark-text-white">
                {t("clientTable.entries")}
              </span>
            </Col>

            <Col xs={12} md={6} className="d-flex justify-content-md-end">
              <InputGroup style={{ maxWidth: "300px" }}>
                <InputGroup.Text className="dark-bg-dark border-secondary">
                  <FaSearch className="dark-text-orange" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder={t(
                    "employeesTable.searchPlaceholder",
                    "Search employees...",
                  )}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="dark-bg-dark dark-text-white border-secondary"
                />
                {searchTerm && (
                  <Button
                    variant="outline-secondary"
                    onClick={clearSearch}
                    title={t("clientTable.clearSearch")}
                    disabled={loading}
                  >
                    ✕
                  </Button>
                )}
              </InputGroup>
            </Col>
          </Row>

          {success && (
            <Alert variant="success" dismissible onClose={() => setSuccess("")}>
              {success}
            </Alert>
          )}

          {/* ── Desktop Accordion ─────────────────────────────── */}
          <Accordion
            className="d-none d-md-block dark-accordion-container mb-5"
            activeKey={activeKey}
            onSelect={(key) => setActiveKey(key)}
          >
            {loading && (
              <div className="text-center py-4">
                <Spinner animation="border" variant="warning" />
                <p className="mt-2 dark-text-muted">{t("common.loading")}</p>
              </div>
            )}

            {!loading && displayData.length === 0 && (
              <p className="text-center p-3 dark-text-muted">
                {searchTerm
                  ? t(
                      "employeesTable.noMatchingEmployees",
                      "No matching employees",
                    )
                  : t("employeesTable.noEmployeesFound", "No employees found")}
                {searchTerm && (
                  <div className="mt-2">
                    <Button
                      variant="outline-warning"
                      size="sm"
                      onClick={clearSearch}
                    >
                      {t("employeesTable.clearSearch", "Clear search")}
                    </Button>
                  </div>
                )}
              </p>
            )}

            {!loading &&
              displayData.map((employee, index) => {
                const overall = employee.average_rating ?? employee.rating ?? 0;
                const totalReviews = employee.total_reviews ?? 0;
                const overallColor = getScoreColor(overall);

                return (
                  <Accordion.Item
                    eventKey={index.toString()}
                    key={employee.id || index}
                  >
                    <Accordion.Header>
                      <div className="dark-artikel-header w-100">
                        <div className="dark-artikel-title-row">
                          <FaUser
                            style={{ color: "#ff7b00", fontSize: "1.4rem" }}
                          />
                          <span className="dark-artikel-title-text">
                            {getFullName(employee)}
                          </span>
                          {employee.can_create_orders === 1 ? (
                            <span className="badge bg-success ms-2">
                              {t("employeesTable.canOrder", "Can order")}
                            </span>
                          ) : (
                            <span className="badge bg-secondary ms-2">
                              {t("employeesTable.cannotOrder", "Read-only")}
                            </span>
                          )}
                          
                          {/* {employee.all_bookings_count && employee.all_bookings_count !== 0  && */}
                            <div 
                              className="bg-info d-flex align-items-center gap-2 px-3 py-1 rounded-pill"
                            >
                              <FaBoxes />
                              {employee.all_bookings_count}
                            </div>
                          {/* } */}
                        </div>

                        <Row className="dark-artikel-data-row align-items-center">
                          <Col xs={12} md={4} className="dark-artikel-col">
                            <div className="dark-artikel-location-item">
                              <div className="address pickup">
                                <FaEnvelope style={{ color: "#ff7b00" }} />
                                <span>
                                  {renderHighlightedText(
                                    employee.email,
                                    searchTerm,
                                  )}
                                </span>
                              </div>
                              <div className="datetime">
                                <FaPhone />
                                <span>{employee.telphone || "N/A"}</span>
                              </div>
                            </div>
                          </Col>

                          <Col xs={12} md={4} className="dark-artikel-col">
                            <div className="dark-artikel-location-item">
                              <div className="address delivery">
                                <FaBuilding style={{ color: "#ff7b00" }} />
                                <span>
                                  {renderHighlightedText(
                                    employee.company_name || "N/A",
                                    searchTerm,
                                  )}
                                </span>
                              </div>
                              <div className="datetime">
                                <FaIdCard />
                                <span>{employee.role_name || "N/A"}</span>
                              </div>
                            </div>
                          </Col>

                          <Col xs={6} md={2} className="dark-artikel-col">
                            <div className="dark-artikel-stat-row">
                              <div
                                className="d-flex align-items-center gap-2 px-3 py-1 rounded-pill"
                                style={{
                                  background: overallColor,
                                  color: "#fff",
                                }}
                                title={`${totalReviews} review${
                                  totalReviews === 1 ? "" : "s"
                                }`}
                              >
                                <FaStar size={12} />
                                <span className="fw-bold" style={{ fontSize: 14 }}>
                                  {overall.toFixed(1)}
                                </span>
                              </div>
                            </div>
                            <div className="dark-artikel-stat-row">
                              <FaBox
                                className="stat-icon"
                                style={{ color: "#888" }}
                              />
                              <span
                                className="stat-value"
                                style={{
                                  color:
                                    employee.can_create_orders === 1
                                      ? "#16a34a"
                                      : "#dc2626",
                                  marginLeft: "5px",
                                }}
                              >
                                {employee.can_create_orders === 1
                                  ? t("common.enabled", "Enabled")
                                  : t("common.disabled", "Disabled")}
                              </span>
                            </div>
                          </Col>

                          <Col xs={6} md={2} className="dark-artikel-col-right">
                            <div
                              className={`dark-artikel-collapse-icon ${
                                activeKey === index.toString() ? "open" : ""
                              }`}
                            >
                              <FaChevronUp
                                className="collapse-chevron"
                                style={{ color: "#ff7b00" }}
                              />
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </Accordion.Header>

                    <Accordion.Body>
                      <div style={{ borderRadius: "12px", padding: "1rem" }}>
                        <Row>
                          <Col md={12}>{renderActions(employee)}</Col>
                        </Row>
                        <Row className="mt-3">
                          <Col md={12}>{renderEmployeeDetails(employee)}</Col>
                        </Row>
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                );
              })}
          </Accordion>

          {/* ── Mobile Cards ──────────────────────────────────── */}
          <div className="d-block d-md-none mb-5">
            {loading && (
              <div className="text-center py-4">
                <Spinner animation="border" variant="warning" />
                <p className="mt-2 dark-text-muted">{t("common.loading")}</p>
              </div>
            )}
            {!loading && displayData.length === 0 && (
              <p className="text-center p-3 dark-text-muted">
                {searchTerm
                  ? t("employeesTable.noMatchingEmployees")
                  : t("employeesTable.noEmployeesFound")}
              </p>
            )}

            {!loading &&
              displayData.map((employee, index) => {
                const overall = employee.average_rating ?? employee.rating ?? 0;
                const totalReviews = employee.total_reviews ?? 0;
                const overallColor = getScoreColor(overall);

                return (
                  <div key={employee.id || index} className="dark-mobile-card">
                    <div
                      className="dark-mobile-card-header"
                      style={{ flexDirection: "column", gap: "0.5rem" }}
                    >
                      <div className="d-flex justify-content-between align-items-center w-100">
                        <div className="d-flex align-items-center gap-2">
                          <FaUser style={{ color: "#ff7b00" }} />
                          <span
                            className="text-orange fw-bold"
                            style={{ fontSize: "0.95rem" }}
                          >
                            {getFullName(employee)}
                          </span>
                        </div>
                        <div className="d-flex gap-2">
                          {employee.can_create_orders === 1 ? (
                            <span className="badge bg-success align-content-center">
                              {t("employeesTable.canOrder", "Can order")}
                            </span>
                          ) : (
                            <span className="badge bg-secondary">
                              {t("employeesTable.cannotOrder", "Read-only")}
                            </span>
                          )}
                          <div
                            className="d-flex align-items-center gap-2 px-3 py-1 rounded-pill"
                            style={{
                              background: overallColor,
                              color: "#fff",
                            }}
                            title={`${totalReviews} review${
                              totalReviews === 1 ? "" : "s"
                            }`}
                          >
                            <FaStar size={12} />
                            <span className="fw-bold" style={{ fontSize: 14 }}>
                              {overall.toFixed(1)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="d-flex flex-column gap-1 w-100 mt-2">
                        <div className="d-flex align-items-center gap-2">
                          <FaEnvelope style={{ color: "#ff7b00" }} />
                          <span
                            className="dark-text-white"
                            style={{ fontSize: "0.85rem" }}
                          >
                            {employee.email || "N/A"}
                          </span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <FaPhone style={{ color: "#ff7b00" }} />
                          <span
                            className="dark-text-white"
                            style={{ fontSize: "0.85rem" }}
                          >
                            {employee.telphone || "N/A"}
                          </span>
                        </div>
                        {employee.company_name && (
                          <div className="d-flex align-items-center gap-2">
                            <FaBuilding style={{ color: "#ff7b00" }} />
                            <span
                              className="dark-text-white"
                              style={{ fontSize: "0.85rem" }}
                            >
                              {employee.company_name}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div
                      className="dark-mobile-card-body"
                      style={{ textAlign: "center", paddingTop: "1rem" }}
                    >

                      <div
                        className="dark-mobile-footer"
                        style={{
                          justifyContent: "center",
                          borderTop: "none",
                          paddingTop: "0",
                        }}
                      >
                        <button
                          className="dark-mobile-details-btn"
                          onClick={() => toggleOffCanvas(index)}
                          style={{
                            width: "100%",
                            maxWidth: "300px",
                            padding: "12px 24px",
                            fontSize: "0.95rem",
                          }}
                        >
                          {t("driverRequest.buttons.details", "Details")}
                          <FaChevronDown />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>

          {/* ── Mobile OffCanvas ──────────────────────────────── */}
          {!loading &&
            displayData.map((employee, index) => (
              <div key={`offcanvas-${index}`}>
                <div
                  className={`offcanvas-overlay ${
                    offCanvasOpen === index ? "show" : ""
                  }`}
                  onClick={() => setOffCanvasOpen(null)}
                />

                <div
                  className={`offcanvas-accordion-body ${
                    offCanvasOpen === index ? "open" : ""
                  }`}
                >
                  <button
                    className="offcanvas-close-btn"
                    onClick={() => setOffCanvasOpen(null)}
                  >
                    <FaTimes />
                  </button>

                  <Card
                    className="offcanvas-card dark-section"
                    style={{
                      background:
                        "linear-gradient(145deg, #2a2a2a 0%, #1e1e1e 50%, #252525 100%)",
                      border: "1px solid #3a3a3a",
                    }}
                  >
                    <Card.Body>
                      <div className="dark-section-title mb-3">
                        <FaUser style={{ color: "#ff7b00" }} />
                        <span className="dark-text-white">
                          {getFullName(employee)}
                        </span>
                      </div>

                      {renderActions(employee)}

                      <div className="mt-3">{renderEmployeeDetails(employee)}</div>
                    </Card.Body>
                  </Card>
                </div>
              </div>
            ))}

          {/* ── Pagination ────────────────────────────────────── */}
          {!loading && pagination.last_page > 0 && !searchTerm && (
            <PaginationComponent
              currentPage={pagination.current_page}
              lastPage={pagination.last_page}
              onPageChange={handlePageChange}
              disabled={loading}
            />
          )}
        </div>

        {/* ── Create Employee Form ───────────────────────────── */}
        {userType === "client" && (
          <div className="dark-section shadow-sm rounded-3 p-4 mt-4">
            <div className="dark-section-title mb-4">
              <FaUser />
              <span style={{ fontSize: "1.2rem" }}>
                {t("employeesTable.addNewEmployee", "Add New Employee")}
              </span>
            </div>

            <Form onSubmit={handleSubmit}>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Label className="dark-text-white">
                    {t("RegisterClientPage.form_fields.company_name")}
                  </Form.Label>
                  <Form.Control
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleInputChange}
                    isInvalid={!!validationErrors.company_name}
                    className="dark-bg-dark dark-text-white border-secondary"
                  />
                  {validationErrors.company_name && (
                    <div className="text-danger small mt-1">
                      {validationErrors.company_name[0]}
                    </div>
                  )}
                </Col>

                <Col md={6}>
                  <Form.Label className="dark-text-white">
                    {t("RegisterClientPage.form_fields.first_name")}
                  </Form.Label>
                  <Form.Control
                    name="firstname"
                    value={formData.firstname}
                    onChange={handleInputChange}
                    required
                    isInvalid={!!validationErrors.firstname}
                    className="dark-bg-dark dark-text-white border-secondary"
                  />
                  {validationErrors.firstname && (
                    <div className="text-danger small mt-1">
                      {validationErrors.firstname[0]}
                    </div>
                  )}
                </Col>

                <Col md={6}>
                  <Form.Label className="dark-text-white">
                    {t("RegisterClientPage.form_fields.last_name")}
                  </Form.Label>
                  <Form.Control
                    name="lastname"
                    value={formData.lastname}
                    onChange={handleInputChange}
                    required
                    isInvalid={!!validationErrors.lastname}
                    className="dark-bg-dark dark-text-white border-secondary"
                  />
                  {validationErrors.lastname && (
                    <div className="text-danger small mt-1">
                      {validationErrors.lastname[0]}
                    </div>
                  )}
                </Col>

                <Col md={6}>
                  <Form.Label className="dark-text-white">
                    {t("RegisterClientPage.form_fields.nickname")}
                  </Form.Label>
                  <Form.Control
                    name="nicname"
                    value={formData.nicname}
                    onChange={handleInputChange}
                    type="tel"
                    isInvalid={!!validationErrors.nicname}
                    className="dark-bg-dark dark-text-white border-secondary"
                  />
                  {validationErrors.nicname && (
                    <div className="text-danger small mt-1">
                      {validationErrors.nicname[0]}
                    </div>
                  )}
                </Col>

                <Col md={6}>
                  <Form.Label className="dark-text-white">
                    {t("RegisterClientPage.form_fields.street_address")}
                  </Form.Label>
                  <Form.Control
                    name="street_address"
                    value={formData.street_address}
                    onChange={handleInputChange}
                    required
                    isInvalid={!!validationErrors.street_address}
                    className="dark-bg-dark dark-text-white border-secondary"
                  />
                  {validationErrors.street_address && (
                    <div className="text-danger small mt-1">
                      {validationErrors.street_address[0]}
                    </div>
                  )}
                </Col>

                <Col md={6}>
                  <Form.Label className="dark-text-white">
                    {t("RegisterClientPage.form_fields.address_one")}
                  </Form.Label>
                  <Form.Control
                    name="addressone"
                    value={formData.addressone}
                    onChange={handleInputChange}
                    required
                    isInvalid={!!validationErrors.addressone}
                    className="dark-bg-dark dark-text-white border-secondary"
                  />
                  {validationErrors.addressone && (
                    <div className="text-danger small mt-1">
                      {validationErrors.addressone[0]}
                    </div>
                  )}
                </Col>

                <Col md={6}>
                  <Form.Label className="dark-text-white">
                    {t("RegisterClientPage.form_fields.postal_code")}
                  </Form.Label>
                  <Form.Control
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleInputChange}
                    required
                    isInvalid={!!validationErrors.postal_code}
                    className="dark-bg-dark dark-text-white border-secondary"
                  />
                  {validationErrors.postal_code && (
                    <div className="text-danger small mt-1">
                      {validationErrors.postal_code[0]}
                    </div>
                  )}
                </Col>

                <Col md={6}>
                  <Form.Label className="dark-text-white">
                    {t("RegisterClientPage.form_fields.email")}
                  </Form.Label>
                  <Form.Control
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    type="email"
                    isInvalid={!!validationErrors.email}
                    className="dark-bg-dark dark-text-white border-secondary"
                  />
                  {validationErrors.email && (
                    <div className="text-danger small mt-1">
                      {validationErrors.email[0]}
                    </div>
                  )}
                </Col>

                <Col md={6}>
                  <Form.Label className="dark-text-white">
                    {t("RegisterClientPage.form_fields.password")}
                  </Form.Label>
                  <div className="password-input-wrapper">
                    <Form.Control
                      type={showPasswords.password ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      isInvalid={!!validationErrors.password}
                      className="dark-bg-dark dark-text-white border-secondary password-input"
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => togglePassword("password")}
                      tabIndex={-1}
                    >
                      {showPasswords.password ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>

                  {formData.password && (
                    <div className="mt-2">
                      <div className="d-flex justify-content-between mb-1">
                        <small className="text-muted">
                          {t("common.passwordStrength", "Password strength")}
                        </small>
                        <small
                          style={{
                            color: getPasswordStrength(formData.password).color,
                            fontWeight: 600,
                          }}
                        >
                          {getPasswordStrength(formData.password).label}
                        </small>
                      </div>
                      <div className="password-strength-bar">
                        <div
                          className="password-strength-fill"
                          style={{
                            width: `${
                              (getPasswordStrength(formData.password).level / 4) *
                              100
                            }%`,
                            backgroundColor: getPasswordStrength(
                              formData.password,
                            ).color,
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {validationErrors.password && (
                    <div className="text-danger small mt-1">
                      {validationErrors.password[0]}
                    </div>
                  )}
                </Col>

                <Col md={6}>
                  <Form.Label className="dark-text-white">
                    {t("vehiclesTable.passwordConfirm", "Confirm Password")}
                  </Form.Label>
                  <div className="password-input-wrapper">
                    <Form.Control
                      type={
                        showPasswords.password_confirmation
                          ? "text"
                          : "password"
                      }
                      name="password_confirmation"
                      value={formData.password_confirmation}
                      onChange={handleInputChange}
                      isInvalid={!!validationErrors.password_confirmation}
                      className="dark-bg-dark dark-text-white border-secondary"
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => togglePassword("password_confirmation")}
                      tabIndex={-1}
                    >
                      {showPasswords.password_confirmation ? (
                        <FaEyeSlash />
                      ) : (
                        <FaEye />
                      )}
                    </button>
                  </div>
                  {validationErrors.password_confirmation && (
                    <div className="text-danger small mt-1">
                      {validationErrors.password_confirmation[0]}
                    </div>
                  )}
                </Col>
              </Row>

              <div className="d-flex justify-content-center align-items-baseline gap-3 mt-5">
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={resetForm}
                  disabled={formLoading}
                  style={{ borderRadius: "30px", padding: "8px 30px" }}
                  className="dark-text-white"
                >
                  {t("actions.reset")}
                </Button>

                <Button
                  type="submit"
                  className="bg-orange dark-btn-cta"
                  style={{
                    border: "none",
                    borderRadius: "30px",
                    padding: "8px 30px",
                    fontWeight: "bold",
                    width: "auto",
                  }}
                  disabled={formLoading}
                >
                  {formLoading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        className="me-2"
                      />
                      {t("common.saving")}...
                    </>
                  ) : (
                    t("actions.save")
                  )}
                </Button>
              </div>
            </Form>
          </div>
        )}

        {/* ── Edit Employee Modal ────────────────────────────── */}
        <Modal
          show={showEditModal}
          onHide={handleCloseEdit}
          centered
          size="lg"
          backdrop="static"
        >
          <Modal.Header closeButton className=" border-secondary">
            <Modal.Title className="">
              <FaEdit className="me-2" style={{ color: "#ff7b00" }} />
              {t("employeesTable.editEmployee", "Edit Employee")}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body className="">
            <Form onSubmit={handleEditSubmit}>
              <Row className="g-3">
                <Col md={12}>
                  <Form.Label className="">
                    {t("RegisterClientPage.form_fields.company_name")}
                  </Form.Label>
                  <Form.Control
                    name="company_name"
                    value={editFormData.company_name}
                    onChange={handleEditInputChange}
                    className="  border-secondary"
                  />
                </Col>

                <Col md={6}>
                  <Form.Label className="">
                    {t("RegisterClientPage.form_fields.first_name")}
                  </Form.Label>
                  <Form.Control
                    name="firstname"
                    value={editFormData.firstname}
                    onChange={handleEditInputChange}
                    isInvalid={!!editErrors.firstname}
                    className="  border-secondary"
                  />
                  {editErrors.firstname && (
                    <div className="text-danger small mt-1">
                      {editErrors.firstname[0]}
                    </div>
                  )}
                </Col>

                <Col md={6}>
                  <Form.Label className="">
                    {t("RegisterClientPage.form_fields.last_name")}
                  </Form.Label>
                  <Form.Control
                    name="lastname"
                    value={editFormData.lastname}
                    onChange={handleEditInputChange}
                    isInvalid={!!editErrors.lastname}
                    className="  border-secondary"
                  />
                  {editErrors.lastname && (
                    <div className="text-danger small mt-1">
                      {editErrors.lastname[0]}
                    </div>
                  )}
                </Col>

                <Col md={6}>
                  <Form.Label className="">
                    {t("RegisterClientPage.form_fields.nickname")}
                  </Form.Label>
                  <Form.Control
                    name="nicname"
                    value={editFormData.nicname}
                    onChange={handleEditInputChange}
                    className="  border-secondary"
                  />
                </Col>

                <Col md={6}>
                  <Form.Label className="">
                    {t("RegisterClientPage.form_fields.email")}
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={editFormData.email}
                    onChange={handleEditInputChange}
                    isInvalid={!!editErrors.email}
                    className="  border-secondary"
                  />
                  {editErrors.email && (
                    <div className="text-danger small mt-1">
                      {editErrors.email[0]}
                    </div>
                  )}
                </Col>

                <Col md={6}>
                  <Form.Label className="">
                    {t("RegisterClientPage.form_fields.street_address")}
                  </Form.Label>
                  <Form.Control
                    name="street_address"
                    value={editFormData.street_address}
                    onChange={handleEditInputChange}
                    className="  border-secondary"
                  />
                </Col>

                <Col md={6}>
                  <Form.Label className="">
                    {t("RegisterClientPage.form_fields.address_one")}
                  </Form.Label>
                  <Form.Control
                    name="addressone"
                    value={editFormData.addressone}
                    onChange={handleEditInputChange}
                    className="  border-secondary"
                  />
                </Col>

                <Col md={6}>
                  <Form.Label className="">
                    {t("RegisterClientPage.form_fields.postal_code")}
                  </Form.Label>
                  <Form.Control
                    name="postal_code"
                    value={editFormData.postal_code}
                    onChange={handleEditInputChange}
                    className="  border-secondary"
                  />
                </Col>

                <Col md={6}>
                  <Form.Label className="">
                    {t("employeesTable.newPassword", "New password")}{" "}
                    <small className="dark-text-muted">
                      ({t("common.optional", "optional")})
                    </small>
                  </Form.Label>
                  <div className="password-input-wrapper">
                    <Form.Control
                      type={showPasswords.password ? "text" : "password"}
                      name="password"
                      value={editFormData.password}
                      onChange={handleEditInputChange}
                      isInvalid={!!editErrors.password}
                      className="  border-secondary password-input"
                      placeholder={t(
                        "common.leaveEmpty",
                        "Leave empty to keep current",
                      )}
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => togglePassword("password")}
                      tabIndex={-1}
                    >
                      {showPasswords.password ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  {editErrors.password && (
                    <div className="text-danger small mt-1">
                      {editErrors.password[0]}
                    </div>
                  )}
                </Col>

                <Col md={6}>
                  <Form.Label className="">
                    {t("vehiclesTable.passwordConfirm")}
                  </Form.Label>
                  <div className="password-input-wrapper">
                    <Form.Control
                      type={
                        showPasswords.password_confirmation
                          ? "text"
                          : "password"
                      }
                      name="password_confirmation"
                      value={editFormData.password_confirmation}
                      onChange={handleEditInputChange}
                      isInvalid={!!editErrors.password_confirmation}
                      className="  border-secondary"
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => togglePassword("password_confirmation")}
                      tabIndex={-1}
                    >
                      {showPasswords.password_confirmation ? (
                        <FaEyeSlash />
                      ) : (
                        <FaEye />
                      )}
                    </button>
                  </div>
                  {editErrors.password_confirmation && (
                    <div className="text-danger small mt-1">
                      {editErrors.password_confirmation[0]}
                    </div>
                  )}
                </Col>
              </Row>

              <div className="d-flex justify-content-center gap-3 mt-4">
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={handleCloseEdit}
                  disabled={editLoading}
                  style={{ borderRadius: "30px", padding: "8px 30px" }}
                  className=""
                >
                  {t("actions.cancel")}
                </Button>

                <Button
                  type="submit"
                  className="bg-orange"
                  disabled={editLoading}
                  style={{
                    border: "none",
                    borderRadius: "30px",
                    padding: "8px 30px",
                    fontWeight: "bold",
                  }}
                >
                  {editLoading ? (
                    <>
                      <Spinner
                        as="span"
                        size="sm"
                        animation="border"
                        className="me-2"
                      />
                      {t("common.saving", "Saving...")}
                    </>
                  ) : (
                    <>
                      <FaEdit className="me-2" />
                      {t("actions.save", "Save")}
                    </>
                  )}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      </Container>

      {(userType === "client" ||
        userType === "driver" ||
        userType === "driver_company") && <ClientChatBox userType={userType} />}
    </>
  );
}